import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/**
 * Generic full-tile overlay for async states (loading, error).
 * Purely presentational — owns no polling or retry logic.
 *
 * Used by:
 *   - MediaItemWithRetry  (variant="loading" while thumbnail generates)
 *   - DataItemCard        (variant="loading"|"error" for spatial dataset status)
 *
 * §6c — shared TileStatusOverlay (generalised from MediaItemWithRetry's
 * inline overlay so media tiles and spatial tiles share one visual).
 */
export interface TileStatusOverlayProps {
  /** "loading" shows a spinner + pulsing skeleton; "error" shows a red icon */
  variant: "loading" | "error";
  /** Primary label, e.g. "PROCESSING…" or "FAILED" */
  label: string;
  /** Optional secondary line, e.g. retry count or error excerpt */
  subLabel?: string;
}

export const TileStatusOverlay = ({ variant, label, subLabel }: TileStatusOverlayProps) => {
  const isError = variant === "error";

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        backgroundColor: isError ? "error.dark" : "grey.700",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.4rem",
        padding: "0.5rem",
        zIndex: 10,
        // Do NOT block pointer events so controls underneath (select, etc.)
        // remain reachable. Callers that need full interception can override.
        pointerEvents: "none"
      }}
    >
      {/* Pulsing skeleton tint (loading only) */}
      {!isError && (
        <Skeleton
          variant='rectangular'
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "primary.main",
            opacity: 0.3,
            zIndex: 0
          }}
        />
      )}

      {/* Icon / spinner */}
      <Box sx={{ zIndex: 1 }}>
        {isError ? (
          <ErrorOutlineIcon sx={{ color: "common.white", fontSize: 20 }} />
        ) : (
          <CircularProgress size={16} sx={{ color: "common.white" }} />
        )}
      </Box>

      {/* Label */}
      <Typography align='center' variant='body2' fontWeight={500} color='common.white' sx={{ zIndex: 1, lineHeight: 1.2 }}>
        {label}
      </Typography>

      {/* Sub-label (retry count, error excerpt, etc.) */}
      {subLabel && (
        <Typography align='center' variant='caption' color='common.white' sx={{ zIndex: 1, opacity: 0.8, lineHeight: 1.2 }}>
          {subLabel}
        </Typography>
      )}
    </Box>
  );
};
